import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import MainSidebar from "@/shared/MainSidebar";
import StatCard from "../components/StatCards/statCard";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../@/components/ui/tabs";
import { Button } from "../../@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../@/components/ui/dropdown-menu";
import CohortDataTable from "../components/cohortDataTable";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/apiRequest";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import { useSelector } from "react-redux";
import { fromZonedTime } from "date-fns-tz";
import AddCohorts from "@/forms/addCohorts";
import EditCohorts from "@/forms/editCohort";
import DeleteCohort from "@/forms/deleteCohort";
import BulkEnrollStudents from "@/forms/bulkEnrollStudents";
import CohortBulkEnrollDialog from "@/forms/cohortBulkEnrollDialog";
import StudentTable from "../components/studentTable";
import { EmptyState } from "../components/emptyState";
import { Spinner } from "../components/spinner";
import TableToolbar from "../components/tableToolbar";
import { BarChart3, ChevronDown, Plus, UserCheck, Users, BookOpenIcon } from "lucide-react";

// Cohort dates are returned as strings like "2025-11-26T00:00:00".
// Interpret them as Africa/Nairobi wall-clock to avoid viewer-timezone drift.
const COHORT_SOURCE_TIMEZONE = "Africa/Nairobi";
function parseCohortDateInSourceTz(value, { endOfDay = false } = {}) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw;
  const [y, m, d] = datePart.split("-").map((n) => Number(n));
  if (![y, m, d].every((n) => Number.isFinite(n))) return null;
  const wallClock = endOfDay
    ? new Date(y, m - 1, d, 23, 59, 59, 999)
    : new Date(y, m - 1, d, 0, 0, 0, 0);
  if (Number.isNaN(wallClock.getTime())) return null;
  const absolute = fromZonedTime(wallClock, COHORT_SOURCE_TIMEZONE);
  return absolute && !Number.isNaN(absolute.getTime()) ? absolute : null;
}

function EnrolmentsSkeleton({ rows = 6 }) {
  return (
    <div className="w-full rounded-md border border-(--neutral-300) bg-(--surface-panel) overflow-hidden">
      {/* Table header */}
      <div className="px-4 py-3 border-b border-(--neutral-300) bg-(--neutral-50)">
        <div className="flex items-center gap-4">
          <div className="h-3 w-32 rounded bg-(--neutral-100) animate-pulse" />
          <div className="h-3 w-24 rounded bg-(--neutral-100) animate-pulse" style={{ animationDelay: '0.1s' }} />
          <div className="h-3 w-36 rounded bg-(--neutral-100) animate-pulse" style={{ animationDelay: '0.2s' }} />
          <div className="h-3 w-20 rounded bg-(--neutral-100) animate-pulse ml-auto" style={{ animationDelay: '0.3s' }} />
        </div>
      </div>
      
      {/* Table rows */}
      <div className="divide-y divide-(--neutral-100)">
        {Array.from({ length: rows }).map((_, idx) => (
          <div key={idx} className="px-4 py-3.5 flex items-center gap-4">
            {/* Varied widths for more realistic appearance */}
            <div 
              className="h-4 rounded bg-(--neutral-100) animate-pulse" 
              style={{ 
                width: `${80 + Math.random() * 40}px`,
                animationDelay: `${idx * 0.05}s` 
              }} 
            />
            <div 
              className="h-4 rounded bg-(--neutral-100) animate-pulse" 
              style={{ 
                width: `${100 + Math.random() * 60}px`,
                animationDelay: `${idx * 0.05 + 0.1}s` 
              }} 
            />
            <div 
              className="h-4 flex-1 max-w-md rounded bg-(--neutral-100) animate-pulse" 
              style={{ animationDelay: `${idx * 0.05 + 0.2}s` }} 
            />
            <div 
              className="h-6 w-20 rounded-full bg-(--neutral-100) animate-pulse" 
              style={{ animationDelay: `${idx * 0.05 + 0.3}s` }} 
            />
          </div>
        ))}
      </div>
      
      {/* Footer pagination hint */}
      <div className="px-4 py-3 border-t border-(--neutral-300) bg-(--neutral-50)">
        <div className="flex items-center justify-between">
          <div className="h-3 w-40 rounded bg-(--neutral-100) animate-pulse" />
          <div className="flex gap-2">
            <div className="h-8 w-8 rounded bg-(--neutral-100) animate-pulse" />
            <div className="h-8 w-8 rounded bg-(--neutral-100) animate-pulse" style={{ animationDelay: '0.1s' }} />
            <div className="h-8 w-8 rounded bg-(--neutral-100) animate-pulse" style={{ animationDelay: '0.2s' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ManageCohorts({
  scope: scopeProp, // 'all' | 'tm' | undefined (auto)
  showCohortsTab: showCohortsTabProp,
  allowEnrolmentActions: allowEnrolmentActionsProp,
  segmentStudentsByStatus: segmentStudentsByStatusProp,
} = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const roles = useSelector((s) => s.auth.roles) || [];
  const isAdmin = roles.includes("Admin");
  const isManager = roles.includes("Manager");
  const isPrivileged = isAdmin || isManager;
  const pageTitle = isPrivileged ? "Manage Cohorts" : "Track My Students";

  const readModeFlags = () => {
    if (typeof window === "undefined") return { adminMode: false, managerMode: false };
    return {
      adminMode: window.localStorage.getItem("zindua_admin_mode") === "on",
      managerMode: window.localStorage.getItem("zindua_manager_mode") === "on",
    };
  };

  const [modeFlags, setModeFlags] = useState(() => readModeFlags());
  useEffect(() => {
    const handle = () => setModeFlags(readModeFlags());
    window.addEventListener("zindua-mode-changed", handle);
    return () => window.removeEventListener("zindua-mode-changed", handle);
  }, []);

  const scope = useMemo(() => {
    if (scopeProp === "all" || scopeProp === "tm") return scopeProp;
    // Auto mode:
    // - Admins see all cohorts only when Admin Mode is ON
    // - Managers see all cohorts only when Manager Mode is ON
    // - Everyone else defaults to TM/instructor cohorts
    if (isAdmin) return modeFlags.adminMode ? "all" : "tm";
    if (isManager) return modeFlags.managerMode ? "all" : "tm";
    return "tm";
  }, [scopeProp, isAdmin, isManager, modeFlags.adminMode, modeFlags.managerMode]);

  const isPrivilegedModeOn = useMemo(() => {
    if (!isPrivileged) return false;
    if (isAdmin) return Boolean(modeFlags.adminMode);
    if (isManager) return Boolean(modeFlags.managerMode);
    return false;
  }, [isPrivileged, isAdmin, isManager, modeFlags.adminMode, modeFlags.managerMode]);

  const showCohortsTab =
    typeof showCohortsTabProp === "boolean"
      ? showCohortsTabProp
      : scope !== "tm";

  const allowEnrolmentActions =
    typeof allowEnrolmentActionsProp === "boolean"
      ? allowEnrolmentActionsProp
      : isPrivilegedModeOn;

  const segmentStudentsByStatus =
    typeof segmentStudentsByStatusProp === "boolean"
      ? segmentStudentsByStatusProp
      : true;

  const urlTab =
    typeof window !== "undefined"
      ? new URLSearchParams(location.search).get("tab")
      : null;
  const initialTabCandidate =
    (location.state && location.state.tab) || urlTab || "active-students";
  const normalizedTabCandidate =
    initialTabCandidate === "previous-students"
      ? "all-students"
      : initialTabCandidate;
  const initialTab =
    // In instructor/TM mode the Cohorts tab can be hidden, so never land there.
    !showCohortsTab && normalizedTabCandidate === "cohorts"
      ? "active-students"
      : normalizedTabCandidate;
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (!urlTab) return;

    const normalizedFromUrl = urlTab === "previous-students" ? "all-students" : urlTab;
    const nextTab =
      !showCohortsTab && normalizedFromUrl === "cohorts"
        ? "active-students"
        : normalizedFromUrl;

    const allowedTabs = new Set(
      showCohortsTab
        ? ["active-students", "all-students", "cohorts"]
        : ["active-students", "all-students"]
    );

    if (!allowedTabs.has(nextTab)) return;
    if (nextTab === activeTab) return;
    setActiveTab(nextTab);
  }, [urlTab, showCohortsTab, activeTab]);
  const [cohorts, setCohorts] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enrolmentsByCohortId, setEnrolmentsByCohortId] = useState({});
  const [enrolmentsLoadingByCohortId, setEnrolmentsLoadingByCohortId] =
    useState({});
  const [expandedByCohortId, setExpandedByCohortId] = useState({});
  const [expandedTouchedByCohortId, setExpandedTouchedByCohortId] =
    useState({});
  const [bulkEnrollPrefillCohortId, setBulkEnrollPrefillCohortId] =
    useState(null);
  const enrolmentsRequestIdRef = useRef(0);
  const [selectedCourse, setSelectedCourse] = useState("ALL");
  const [selectedCohortId, setSelectedCohortId] = useState("ALL"); // Default to "ALL"
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCohort, setEditCohort] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCohort, setDeleteCohort] = useState(null);
  const [bulkEnrollOpen, setBulkEnrollOpen] = useState(false);
  const [cohortBulkDialogOpen, setCohortBulkDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [courseFilter, setCourseFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("name");
  const [cohortSearch, setCohortSearch] = useState("");
  const [cohortSort, setCohortSort] = useState("name");
  const [cohortTypeFilter, setCohortTypeFilter] = useState("ALL");
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  // For students tab
  const [pendingSelectedCohortId, setPendingSelectedCohortId] =
    useState(selectedCohortId);
  const [pendingSelectedCourse, setPendingSelectedCourse] =
    useState(selectedCourse);
  const [pendingSortBy, setPendingSortBy] = useState(sortBy);

  // For cohorts tab
  const [pendingCohortTypeFilter, setPendingCohortTypeFilter] =
    useState(cohortTypeFilter);
  const [pendingCohortSort, setPendingCohortSort] = useState(cohortSort);

  useEffect(() => {
    setPendingSelectedCohortId(selectedCohortId);
  }, [selectedCohortId]);
  useEffect(() => {
    setPendingSelectedCourse(selectedCourse);
  }, [selectedCourse]);
  useEffect(() => {
    setPendingSortBy(sortBy);
  }, [sortBy]);

  useEffect(() => {
    setPendingCohortTypeFilter(cohortTypeFilter);
  }, [cohortTypeFilter]);
  useEffect(() => {
    setPendingCohortSort(cohortSort);
  }, [cohortSort]);

  // Fetch cohorts on mount (and when scope changes)
  useEffect(() => {
    fetchCohorts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Fetch enrolments when cohorts are loaded or filters change
  useEffect(() => {
    if (
      (activeTab === "active-students" || activeTab === "all-students") &&
      cohorts.length > 0
    ) {
      fetchEnrolments(selectedCohortId, selectedCourse);
    }
  }, [activeTab, selectedCohortId, selectedCourse, cohorts]);

  function formatDate(dateString) {
    if (!dateString) return "Not available";
    const date = new Date(dateString);
    if (isNaN(date)) return "Not available";
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  const fetchCohorts = async () => {
    setLoading(true);
    const path = scope === "tm" ? "/cohort/tm" : "/cohort/all";
    const label = scope === "tm" ? "[/cohort/tm]" : "[/cohort/all]";
    try {
      console.log(`${label} fetching cohorts...`);
      const data = await queryClient.fetchQuery({
        queryKey: scope === "tm" ? ["cohort", "tm"] : ["cohort", "all"],
        queryFn: ({ signal }) =>
          apiRequest(getToken, {
            path,
            auth: "required",
            signal,
          }),
      });
      console.log(`${label} response:`, data);
      const fetched =
        (Array.isArray(data) ? data : null) ??
        (Array.isArray(data?.cohorts) ? data.cohorts : null) ??
        (Array.isArray(data?.items) ? data.items : null) ??
        (Array.isArray(data?.data) ? data.data : null) ??
        [];
      const formatted = fetched.map((item) => ({
        id: item.id,
        name: item.name || "Not available",
        type: item.type || "Not available",
        start: formatDate(item.start_date) || "Not available",
        end: formatDate(item.end_date) || "Not available",
        raw: item,
      }));
      setCohorts(formatted);
    } catch (error) {
      console.log(`${label} error:`, error);
      setCohorts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch enrolments per-cohort and progressively update UI.
  const fetchEnrolments = async (cohortId, courseName) => {
    const requestId = ++enrolmentsRequestIdRef.current;
    setEnrolments([]);
    setEnrolmentsByCohortId({});
    setEnrolmentsLoadingByCohortId({});

    try {
      const cohortIdsToFetch =
        cohortId && cohortId !== "ALL"
          ? [Number(cohortId)]
          : (cohorts || []).map((c) => c.id);

      // Set per-group loading state.
      setEnrolmentsLoadingByCohortId(() => {
        const next = {};
        cohortIdsToFetch.forEach((id) => {
          next[id] = true;
        });
        return next;
      });

      const cohortsInOrder = (cohorts || []).filter((c) =>
        cohortIdsToFetch.includes(c.id)
      );

      for (const cohort of cohortsInOrder) {
        if (enrolmentsRequestIdRef.current !== requestId) return;

        const enrolmentsPath = `/cohort/enrolments/${cohort.id}`;
        if (import.meta?.env?.DEV) {
          console.log("[enrolments] fetching", {
            scope,
            path: enrolmentsPath,
            selectedCourse: courseName,
            queryKey: ["cohort", cohort.id, "enrolments"],
          });
        }

        const responseData = await queryClient.fetchQuery({
          queryKey: ["cohort", cohort.id, "enrolments", "skip", 200],
          queryFn: async () => {
            const token = await getToken();
            if (!token) return { enrolments: [] };
            const reqHeaders = {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            };
            const enrolments = await fetchAllPaginatedItems({
              url: api_endpoint + enrolmentsPath,
              token,
              headers: reqHeaders,
              mode: "skip",
              pageSize: 200,
              maxPages: 200,
              dedupe: false,
              selectItems: (data) => data?.enrolments ?? [],
            });
            return { enrolments };
          },
        });

        if (import.meta?.env?.DEV) {
          const topLevelKeys =
            responseData && typeof responseData === "object"
              ? Object.keys(responseData)
              : [];
          const enrolmentsArray = Array.isArray(responseData?.enrolments)
            ? responseData.enrolments
            : null;
          const firstItem =
            enrolmentsArray && enrolmentsArray.length ? enrolmentsArray[0] : null;
          const firstItemKeys =
            firstItem && typeof firstItem === "object" ? Object.keys(firstItem) : [];

          console.log("[enrolments] response shape", {
            path: enrolmentsPath,
            type: Array.isArray(responseData) ? "array" : typeof responseData,
            topLevelKeys,
            enrolmentsCount: enrolmentsArray ? enrolmentsArray.length : 0,
            firstItemKeys,
          });

        }

        console.log(`enrolments for cohort ${cohort.id} (${cohort.name}):`, responseData);

        const cohortEnrolments = Array.isArray(responseData?.enrolments)
          ? responseData.enrolments
          : [];

        const filteredRaw =
          courseName && courseName !== "ALL"
            ? cohortEnrolments.filter((e) => e?.course_name === courseName)
            : cohortEnrolments;

        // De-dupe by (user, cohort, course).
        const deduped = new Map();
        for (const item of filteredRaw || []) {
          const userId = item?.user_id || item?.userId || item?.id;
          const courseKey = item?.course_id || item?.courseId || item?.course_name;
          const key = `${userId ?? "unknown"}|${cohort.id}|${courseKey ?? "unknown"}`;
          if (!deduped.has(key)) deduped.set(key, item);
        }

        const uniqueFetched = Array.from(deduped.values());
        const formattedRows = uniqueFetched.map((item) => {
          const userId = item?.user_id || item?.userId || item?.id;
          const cohortName = item?.cohort_name || cohort?.name || "Not available";
          const courseIdOut = item?.course_id ?? item?.courseId;
          const courseNameOut = item?.course_name || "Not available";
          const totalPoints =
            item?.total_points !== undefined && item?.total_points !== null
              ? item.total_points
              : "Not available";

          return {
            id: userId,
            userId,
            user_id: userId,
            cohort_id: cohort.id,
            course_id: courseIdOut,
            row_id: `${userId ?? "unknown"}|${cohort.id}|${courseNameOut}`,
            cohort: cohortName,
            name:
              item?.user_first_name && item?.user_last_name
                ? `${item.user_first_name} ${item.user_last_name}`
                : "Not available",
            email: item?.user_email || "Not available",
            status: item?.status || "Not available",
            course: courseNameOut,
            actions: "",
            projects: item?.projects || "",
            attendance: item?.attendance || "Not available",
            total: totalPoints,
          };
        });

        setEnrolmentsByCohortId((prev) => ({
          ...prev,
          [cohort.id]: formattedRows,
        }));

        // Keep a flat list for toolbar options and stat summaries.
        setEnrolments((prev) => prev.concat(formattedRows));

        setEnrolmentsLoadingByCohortId((prev) => ({
          ...prev,
          [cohort.id]: false,
        }));

        // Default expansion rules: empty cohorts collapse by default.
        setExpandedByCohortId((prev) => {
          const touched = Boolean(expandedTouchedByCohortId?.[cohort.id]);
          if (touched) return prev;
          if (prev?.[cohort.id] !== undefined) return prev;
          return {
            ...prev,
            [cohort.id]: formattedRows.length > 0,
          };
        });
      }
    } catch (error) {
      setEnrolments([]);
      setEnrolmentsByCohortId({});
      setEnrolmentsLoadingByCohortId({});
    }
  };

  const invalidateCohortQueries = async () => {
    try {
      // Cohorts list + enrolments are all keyed under ["cohort", ...].
      // Global staleTime can cause fetchQuery() to return cached data,
      // so we invalidate before re-fetching after mutations.
      await queryClient.invalidateQueries({ queryKey: ["cohort"], exact: false });
    } catch (err) {
      if (import.meta?.env?.DEV) {
        console.warn("[cohort] invalidateQueries failed", err);
      }
    }
  };

  const refreshCohorts = async () => {
    await invalidateCohortQueries();
    fetchCohorts();
  };

  const refreshEnrolments = async () => {
    await invalidateCohortQueries();
    fetchEnrolments(selectedCohortId, selectedCourse);
  };

  const refreshCohortData = async () => {
    await invalidateCohortQueries();
    fetchCohorts();
    fetchEnrolments(selectedCohortId, selectedCourse);
  };

  const cohortOptions = useMemo(() => {
    return [
      { value: "ALL", label: "All Cohorts" },
      ...cohorts.map((cohort) => ({
        value: cohort.id.toString(),
        label: cohort.name,
      })),
    ];
  }, [cohorts]);

  const courseOptions = useMemo(() => {
    // Get unique course names from enrolments
    const uniqueCourses = [
      ...new Set(enrolments.map((e) => e.course).filter(Boolean)),
    ];
    return [
      { value: "ALL", label: "All Courses" },
      ...uniqueCourses.map((course) => ({
        value: course,
        label: course,
      })),
    ];
  }, [enrolments]);

  const cohortTypeOptions = useMemo(() => {
    // Get unique cohort types
    const uniqueTypes = [
      ...new Set(cohorts.map((c) => c.type).filter(Boolean)),
    ];
    return ["ALL", ...uniqueTypes];
  }, [cohorts]);

  const studentFilterConfig = [
    {
      label: "Filter by Cohort",
      value: selectedCohortId,
      onChange: setSelectedCohortId,
      onClear: () => setSelectedCohortId("ALL"),
      options: cohortOptions,
    },
    {
      label: "Filter by Course",
      value: selectedCourse,
      onChange: setSelectedCourse,
      onClear: () => setSelectedCourse("ALL"),
      options: courseOptions,
    },
  ];

  const studentSortConfig = [
    {
      label: "Sort by",
      value: sortBy,
      onChange: setSortBy,
      onClear: () => setSortBy("name"),
      options: [
        { value: "name", label: "Name" },
        { value: "course", label: "Course" },
      ],
    },
  ];

  // For Cohorts tab
  const cohortFilterConfig = [
    {
      label: "Filter by Type",
      value: cohortTypeFilter,
      onChange: setCohortTypeFilter,
      onClear: () => setCohortTypeFilter("ALL"),
      options: cohortTypeOptions.map((type) => ({
        value: type,
        label: type === "ALL" ? "All Types" : type,
      })),
    },
  ];

  const cohortSortConfig = [
    {
      label: "Sort by",
      value: cohortSort,
      onChange: setCohortSort,
      onClear: () => setCohortSort("name"),
      options: [
        { value: "name", label: "Name" },
        { value: "type", label: "Type" },
        { value: "start", label: "Start Date" },
        { value: "end", label: "End Date" },
      ],
    },
  ];

  // Filtering and sorting for students
  const processedStudentData = useMemo(() => {
    let result = enrolments;

    // Tab-level segmentation.
    if (segmentStudentsByStatus && activeTab === "active-students") {
      // "Current Students" is defined by an ACTIVE cohort period (not enrolment.status).
      const nowMs = Date.now();
      const activeCohortIds = new Set(
        (Array.isArray(cohorts) ? cohorts : [])
          .filter((c) => {
            const start = parseCohortDateInSourceTz(c?.raw?.start_date ?? c?.start_date);
            const end = parseCohortDateInSourceTz(c?.raw?.end_date ?? c?.end_date, { endOfDay: true });

            // Missing dates: treat as active (best-effort).
            const startOk = start ? start.getTime() <= nowMs : true;
            const endOk = end ? nowMs <= end.getTime() : true;
            return startOk && endOk;
          })
          .map((c) => c.id)
      );

      result = result.filter((e) => {
        const cid = e?.cohort_id ?? e?.cohortId;
        return cid != null && activeCohortIds.has(Number(cid));
      });
    }

    // Apply search filter
    const rawSearch = (searchTerm || "").trim().toLowerCase();
    if (rawSearch) {
      // Match behavior: like ManagePathways enrolments search.
      // Search across name/email/course only (no status or cohort-code matching).
      const query = rawSearch;
      const tokens = query.split(/\s+/).filter(Boolean);

      result = result.filter((student) => {
        const name = String(student.name || "").toLowerCase();
        const course = String(student.course || "").toLowerCase();
        const email = String(student.email || "").toLowerCase();

        const combined = [name, email, course].filter(Boolean).join(" | ");

        // Require every token to match somewhere (so "jenny margaret" matches
        // even if it's not an exact contiguous substring).
        return tokens.every((t) => combined.includes(t));
      });
    }
    // Apply cohort filter - FIXED: Use cohort name, not ID
    if (selectedCohortId !== "ALL") {
      const selectedCohortName = cohorts.find(
        (c) => c.id.toString() === selectedCohortId
      )?.name;
      if (selectedCohortName) {
        result = result.filter(
          (student) => student.cohort === selectedCohortName
        );
      }
    }

    // Apply course filter - FIXED: Use selectedCourse, not courseFilter
    if (selectedCourse !== "ALL") {
      result = result.filter((student) => student.course === selectedCourse);
    }

    // Apply sorting
    result = [...result].sort((a, b) => {
      // When searching, sort by relevance first.
      if (rawSearch) {
        const query = rawSearch;
        const tokens = query.split(/\s+/).filter(Boolean);

        // Cache computed match signals to keep sort stable and efficient.
        const signalsCache = new Map();
        const getSignals = (student) => {
          const cacheKey =
            student?.id ??
            student?.email ??
            `${student?.name ?? ""}|${student?.cohort ?? ""}|${student?.course ?? ""}`;
          if (signalsCache.has(cacheKey)) return signalsCache.get(cacheKey);

          const name = String(student.name || "").toLowerCase();
          const course = String(student.course || "").toLowerCase();
          const email = String(student.email || "").toLowerCase();

          const tokenAny = (haystack) => tokens.some((t) => haystack.includes(t));
          const tokenAll = (haystack) => tokens.every((t) => haystack.includes(t));
          const startsAny = (haystack) => tokens.some((t) => haystack.startsWith(t));

          const nameExact = Boolean(query) && name === query;
          const nameAll = tokens.length > 0 && tokenAll(name);
          const nameStartsAny = tokens.length > 0 && startsAny(name);
          const nameAny = tokens.length > 0 && tokenAny(name);

          const emailAny = tokens.length > 0 && tokenAny(email);
          const courseAny = tokens.length > 0 && tokenAny(course);

          // Fallback weighted score for ties within the same match bucket.
          let weighted = 0;
          if (nameExact) weighted += 500;
          if (nameAll) weighted += 250;
          if (nameStartsAny) weighted += 75;
          if (nameAny) weighted += 200;
          if (emailAny) weighted += 80;
          if (courseAny) weighted += 60;

          const signals = {
            nameExact,
            nameAll,
            nameStartsAny,
            nameAny,
            emailAny,
            courseAny,
            weighted,
          };

          signalsCache.set(cacheKey, signals);
          return signals;
        };

        const sa = getSignals(a);
        const sb = getSignals(b);

        // Deterministic relevance: name matches always beat non-name matches.
        const boolDiff = (x, y) => Number(y) - Number(x);

        let diff = 0;
        diff = boolDiff(sa.nameExact, sb.nameExact);
        if (diff) return diff;
        diff = boolDiff(sa.nameAll, sb.nameAll);
        if (diff) return diff;
        diff = boolDiff(sa.nameStartsAny, sb.nameStartsAny);
        if (diff) return diff;
        diff = boolDiff(sa.nameAny, sb.nameAny);
        if (diff) return diff;
        diff = boolDiff(sa.emailAny, sb.emailAny);
        if (diff) return diff;
        diff = boolDiff(sa.courseAny, sb.courseAny);
        if (diff) return diff;

        diff = sb.weighted - sa.weighted;
        if (diff !== 0) return diff;

        return String(a.name || "").localeCompare(String(b.name || ""));
      }

      switch (sortBy) {
        case "name":
          return String(a.name || "").localeCompare(String(b.name || ""));
        case "course":
          return String(a.course || "").localeCompare(String(b.course || ""));
        default:
          return 0;
      }
    });

    return result;
  }, [
    enrolments,
    searchTerm,
    selectedCohortId,
    selectedCourse,
    sortBy,
    cohorts,
    activeTab,
    segmentStudentsByStatus,
  ]);

  const cohortsToRender = useMemo(() => {
    const nowMs = Date.now();
    const all = Array.isArray(cohorts) ? cohorts : [];

    const isCohortActiveNow = (c) => {
      const start = parseCohortDateInSourceTz(c?.raw?.start_date ?? c?.start_date);
      const end = parseCohortDateInSourceTz(c?.raw?.end_date ?? c?.end_date, { endOfDay: true });
      const startOk = start ? start.getTime() <= nowMs : true;
      const endOk = end ? nowMs <= end.getTime() : true;
      return startOk && endOk;
    };

    const scopeList =
      segmentStudentsByStatus && activeTab === "active-students"
        ? all.filter(isCohortActiveNow)
        : all;

    if (selectedCohortId && selectedCohortId !== "ALL") {
      const found = scopeList.find((c) => String(c.id) === String(selectedCohortId));
      return found ? [found] : [];
    }

    return scopeList;
  }, [activeTab, cohorts, selectedCohortId, segmentStudentsByStatus]);

  const groupedProcessedData = useMemo(() => {
    const map = new Map();
    for (const item of processedStudentData || []) {
      const cohortIdOut =
        item?.cohort_id ??
        (Array.isArray(cohorts)
          ? cohorts.find((c) => c?.name === item?.cohort)?.id
          : undefined);
      if (cohortIdOut == null) continue;
      if (!map.has(cohortIdOut)) map.set(cohortIdOut, []);
      map.get(cohortIdOut).push(item);
    }
    return map;
  }, [processedStudentData, cohorts]);

  const hasAnyVisibleRows = useMemo(() => {
    return (cohortsToRender || []).some((c) => {
      const rows = groupedProcessedData.get(c.id) || [];
      return rows.length > 0;
    });
  }, [cohortsToRender, groupedProcessedData]);

  const isAnyVisibleGroupLoading = useMemo(() => {
    return (cohortsToRender || []).some((c) =>
      Boolean(enrolmentsLoadingByCohortId?.[c.id]),
    );
  }, [cohortsToRender, enrolmentsLoadingByCohortId]);

  const stats = useMemo(() => {
    const rows = Array.isArray(enrolments) ? enrolments : [];
    const nowMs = Date.now();

    // Stat cards should match the table's notion of an "enrolment" row.
    // Some users can appear multiple times across cohorts/courses.
    const keyFor = (e) =>
      e?.row_id ??
      `${e?.user_id ?? e?.userId ?? e?.id ?? "unknown"}|${
        e?.cohort_id ?? "unknown"
      }|${e?.course_id ?? e?.course ?? "unknown"}`;

    const uniqueAll = new Set(rows.map(keyFor));

    const activeCohortIds = new Set(
      (Array.isArray(cohorts) ? cohorts : [])
        .filter((c) => {
          const start = parseCohortDateInSourceTz(c?.raw?.start_date ?? c?.start_date);
          const end = parseCohortDateInSourceTz(c?.raw?.end_date ?? c?.end_date, { endOfDay: true });
          const startOk = start ? start.getTime() <= nowMs : true;
          const endOk = end ? nowMs <= end.getTime() : true;
          return startOk && endOk;
        })
        .map((c) => c.id)
    );

    const currentRows = rows.filter((e) => {
      const cid = e?.cohort_id ?? e?.cohortId;
      return cid != null && activeCohortIds.has(Number(cid));
    });

    const uniqueCurrent = new Set(currentRows.map(keyFor));

    const totals = currentRows
      .map((e) => Number(e?.total))
      .filter((n) => Number.isFinite(n));
    const avg = totals.length ? totals.reduce((a, b) => a + b, 0) / totals.length : 0;

    return {
      averageScore: Math.round(avg),
      currentEnrolments: uniqueCurrent.size,
      allEnrolments: uniqueAll.size,
    };
  }, [cohorts, enrolments]);

  const toggleGroupExpanded = (cohortId) => {
    setExpandedTouchedByCohortId((prev) => ({ ...prev, [cohortId]: true }));
    setExpandedByCohortId((prev) => {
      const current = prev?.[cohortId];
      const defaultExpanded = true;
      const next = !(current ?? defaultExpanded);
      return { ...prev, [cohortId]: next };
    });
  };

  // Filtering and sorting for cohorts tab
  const cohortSortOptions = [
    { value: "name", label: "Name" },
    { value: "type", label: "Type" },
    { value: "start", label: "Start Date" },
    { value: "end", label: "End Date" },
  ];

  const filteredSortedCohorts = useMemo(() => {
    let result = cohorts;
    if (cohortTypeFilter !== "ALL") {
      result = result.filter((c) => c.type === cohortTypeFilter);
    }
    if (cohortSearch.trim()) {
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(cohortSearch.toLowerCase()) ||
          c.type.toLowerCase().includes(cohortSearch.toLowerCase())
      );
    }
    // Sort by created_at descending (most recent first)
    result = [...result].sort((a, b) => {
      const aDate = new Date(a.raw?.created_at || a.created_at || 0);
      const bDate = new Date(b.raw?.created_at || b.created_at || 0);
      return bDate - aDate;
    });
    // If another sort is selected, apply it after
    if (cohortSort !== "name") {
      result = [...result].sort((a, b) => {
        if (a[cohortSort] < b[cohortSort]) return -1;
        if (a[cohortSort] > b[cohortSort]) return 1;
        return 0;
      });
    }
    return result;
  }, [cohorts, cohortTypeFilter, cohortSearch, cohortSort]);

  const cohortColumns = [
    { key: "name", label: "Name" },
    { key: "type", label: "Type" },
    { key: "start", label: "Start Date" },
    { key: "end", label: "End Date" },
    {
      key: "action",
      label: "Action",
      render: (_value, row) => null,
    },
  ];

  const enrolmentColumns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "status", label: "Status" },
    { key: "cohort", label: "Cohort" },
    { key: "completion", label: "Completion" },
    { key: "score", label: "Score" },
  ];

  // Handlers for filter changes
  const handleCohortFilterChange = (cohortId) => {
    setSelectedCohortId(cohortId);
  };

  const handleCourseFilterChange = (courseName) => {
    setSelectedCourse(courseName);
  };

  return (
    <>
      <div className="flex flex-col w-full min-h-screen">
        <h1 className="text-h3 font-heading text-(--neutral-900)">
          {pageTitle}
        </h1>
        <p className="text-(--neutral-800) font-body">
          Manage every cohort in one place, track students, monitor enrolments,
          and keep cohort details up to date.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          <StatCard
            label="Average Score"
            icon={<BarChart3 className="w-5 h-5 text-(--neutral-900)" />}
            value={<span>{stats.averageScore.toLocaleString()} points</span>}
            subtitle="Across my currently enrolled students"
            hideAction
            className="py-3"
            valueClassName="text-h2"
            subtitleClassName="text-sm text-(--neutral-600)"
          />
          <StatCard
            label="Current Enrolments"
            icon={<UserCheck className="w-5 h-5 text-(--neutral-900)" />}
            value={<span>{stats.currentEnrolments.toLocaleString()} students</span>}
            subtitle="In currently ongoing cohorts"
            hideAction
            className="py-3"
            valueClassName="text-h2"
            subtitleClassName="text-sm text-(--neutral-600)"
          />
          <StatCard
            label="All Enrolments"
            icon={<Users className="w-5 h-5 text-(--neutral-900)" />}
            value={<span>{stats.allEnrolments.toLocaleString()} students</span>}
            subtitle="Across all current/previous cohorts"
            hideAction
            className="py-3"
            valueClassName="text-h2"
            subtitleClassName="text-sm text-(--neutral-600)"
          />
        </div>
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            setActiveTab(value);
            try {
              const params = new URLSearchParams(location.search);
              params.set("tab", value);
              navigate({
                to: `${location.pathname}?${params.toString()}`,
                replace: true,
              });
            } catch (e) {
              console.warn("Failed to update tab in URL", e);
            }
          }}
          className="space-y-4 mt-3"
        >
          <div className="flex flex-col w-full gap-2 sm:flex-col md:flex-row md:items-center md:justify-between">
            <TabsList className="w-full md:w-auto h-12 sm:h-9">
              <TabsTrigger
                value="active-students"
                className="w-full md:w-auto px-2 sm:px-3 py-1 sm:py-0.5 text-xs sm:text-sm text-center leading-tight whitespace-normal flex-col sm:flex-row gap-0 sm:gap-1"
              >
                <span>Current</span>
                <span>Enrolments</span>
              </TabsTrigger>
              <TabsTrigger
                value="all-students"
                className="w-full md:w-auto px-2 sm:px-3 py-1 sm:py-0.5 text-xs sm:text-sm text-center leading-tight whitespace-normal flex-col sm:flex-row gap-0 sm:gap-1"
              >
                <span>All</span>
                <span>Enrolments</span>
              </TabsTrigger>
              {showCohortsTab && (
                <TabsTrigger
                  value="cohorts"
                  className="w-full md:w-auto px-2 sm:px-3 py-0.5 text-xs sm:text-sm text-center leading-tight whitespace-normal"
                >
                  Cohorts
                </TabsTrigger>
              )}
            </TabsList>
            <div className="w-full md:w-auto mt-2 md:mt-0">
              {allowEnrolmentActions &&
                (activeTab === "active-students" || activeTab === "all-students") && (
                <div className="flex flex-col gap-2 w-full md:flex-row md:justify-end md:w-auto">
                  <div className="flex w-full md:w-auto items-stretch gap-0.5">
                    <Button
                      type="button"
                      variant=""
                      className="w-full"
                      onClick={() => {
                        setBulkEnrollPrefillCohortId(null);
                        setBulkEnrollOpen(true);
                      }}
                    >
                      New Enrolment <Plus className="w-4 h-4 ml-2" />
                    </Button>
                    {/* className="flex-1 sm:flex-none w-full sm:w-auto rounded-l-md rounded-r-none bg-(--color-primary) text-white hover:bg-(--color-accent) border border-(--color-primary)" */}
                    {/* <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="default"
                          className="flex-none w-12 md:w-auto rounded-l-none rounded-r-md border border-(--color-primary) bg-(--color-primary) text-white px-0 md:px-3 flex items-center justify-center"
                          aria-label="More enrolment options"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="min-w-55"
                      >
                        <DropdownMenuItem
                          onSelect={(event) => {
                            event.preventDefault();
                            setCohortBulkDialogOpen(true);
                          }}
                        >
                          Bulk Enroll Students
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu> */}
                  </div>
                </div>
              )}
              {showCohortsTab && activeTab === "cohorts" && (
                <AddCohorts refetchCohorts={refreshCohorts} />
              )}
            </div>
          </div>

          {activeTab === "active-students" && (
          <TabsContent value="active-students" className="space-y-3">
            {loading ? (
              <div className="flex justify-center items-center py-12 w-full">
                <Spinner />
              </div>
            ) : (
              <>
                <TableToolbar
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  filters={[
                    {
                      label: "Filter by Cohort",
                      value: pendingSelectedCohortId,
                      onChange: setPendingSelectedCohortId,
                      onClear: () => setPendingSelectedCohortId("ALL"),
                      options: cohortOptions,
                    },
                    {
                      label: "Filter by Course",
                      value: pendingSelectedCourse,
                      onChange: setPendingSelectedCourse,
                      onClear: () => setPendingSelectedCourse("ALL"),
                      options: courseOptions,
                    },
                  ]}
                  sorts={[
                    {
                      label: "Sort by",
                      value: pendingSortBy,
                      onChange: setPendingSortBy,
                      onClear: () => setPendingSortBy("name"),
                      options: [
                        { value: "name", label: "Name" },
                        { value: "course", label: "Course" },
                      ],
                    },
                  ]}
                  onApplyFilters={() => {
                    setSelectedCohortId(pendingSelectedCohortId);
                    setSelectedCourse(pendingSelectedCourse);
                  }}
                  onApplySort={() => {
                    setSortBy(pendingSortBy);
                  }}
                />
                {cohortsToRender.length === 0 ? (
                  <EmptyState
                    icon={BookOpenIcon}
                    title="No students to show"
                    description="No cohort enrolled students assigned to you yet."
                    className="py-10"
                  />
                ) : !isAnyVisibleGroupLoading && !hasAnyVisibleRows ? (
                  <EmptyState
                    icon={BookOpenIcon}
                    title="No students to show"
                    description="No cohort students assigned tou you yet."
                    className="py-10"
                  />
                ) : (
                  <div className="space-y-4">
                    {cohortsToRender.map((cohort) => {
                      const cohortId = cohort.id;
                      const groupHasLoaded =
                        enrolmentsLoadingByCohortId?.[cohortId] === false ||
                        enrolmentsByCohortId?.[cohortId] !== undefined;
                      const isLoading = Boolean(
                        enrolmentsLoadingByCohortId?.[cohortId]
                      );
                      const rows = groupedProcessedData.get(cohortId) || [];
                      const defaultExpanded = groupHasLoaded
                        ? rows.length > 0
                        : true;
                      const isExpanded =
                        expandedByCohortId?.[cohortId] ?? defaultExpanded;

                      return (
                        <div
                          key={cohortId}
                          className="rounded-md"
                        >
                          <div className="px-1 py-2 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1 min-w-0">
                              <Button
                                type="button"
                                variant="ghost"
                                size=""
                                className="px-2 -mt-2 text-(--neutral-900) hover:bg-transparent flex items-center justify-center"
                                onClick={() => toggleGroupExpanded(cohortId)}
                                aria-label={`Toggle cohort ${cohort.name}`}
                              >
                                <ChevronDown
                                  className={`w-6 h-6 transition-transform ${
                                    isExpanded ? "rotate-180" : "rotate-0"
                                  }`}
                                  strokeWidth={3.5}
                                />
                              </Button>
                              <div className="flex items-center gap-1 min-w-0">
                                <h4 className="font-heading text-(--neutral-900) truncate leading-none">
                                  Cohort: {cohort.name}
                                </h4>
                                {allowEnrolmentActions && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size=""
                                    className="p-0 -mt-2 h-auto w-auto text-(--neutral-800) hover:text-(--color-accent) hover:bg-transparent flex items-center justify-center"
                                    title="New Enrolments"
                                    onClick={() => {
                                      setBulkEnrollPrefillCohortId(String(cohortId));
                                      setBulkEnrollOpen(true);
                                    }}
                                    aria-label={`New enrolment for ${cohort.name}`}
                                  >
                                    <Plus className="w-5 h-5" strokeWidth={3.5} />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="px-4 pb-4">
                              {isLoading && rows.length === 0 ? (
                                <EnrolmentsSkeleton />
                              ) : (
                                <StudentTable
                                  data={rows}
                                  cohorts={cohorts}
                                  selectedCohortId={selectedCohortId}
                                  selectedCourse={selectedCourse}
                                  searchTerm={searchTerm}
                                  setSelectedCohortId={setSelectedCohortId}
                                  onEnrollmentDeleted={refreshEnrolments}
                                  onEnrollmentUpdated={refreshEnrolments}
                                  allowEnrolmentActions={allowEnrolmentActions}
                                  hideCohortColumn
                                  showSelectedCount
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </TabsContent>
          )}

          {activeTab === "all-students" && (
          <TabsContent value="all-students" className="space-y-3">
            {loading ? (
              <div className="flex justify-center items-center py-12 w-full">
                <Spinner />
              </div>
            ) : (
              <>
                <TableToolbar
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  filters={[
                    {
                      label: "Filter by Cohort",
                      value: pendingSelectedCohortId,
                      onChange: setPendingSelectedCohortId,
                      onClear: () => setPendingSelectedCohortId("ALL"),
                      options: cohortOptions,
                    },
                    {
                      label: "Filter by Course",
                      value: pendingSelectedCourse,
                      onChange: setPendingSelectedCourse,
                      onClear: () => setPendingSelectedCourse("ALL"),
                      options: courseOptions,
                    },
                  ]}
                  sorts={[
                    {
                      label: "Sort by",
                      value: pendingSortBy,
                      onChange: setPendingSortBy,
                      onClear: () => setPendingSortBy("name"),
                      options: [
                        { value: "name", label: "Name" },
                        { value: "course", label: "Course" },
                      ],
                    },
                  ]}
                  onApplyFilters={() => {
                    setSelectedCohortId(pendingSelectedCohortId);
                    setSelectedCourse(pendingSelectedCourse);
                  }}
                  onApplySort={() => {
                    setSortBy(pendingSortBy);
                  }}
                />
                {cohortsToRender.length === 0 ? (
                  <EmptyState
                    icon={BookOpenIcon}
                    title="No students to show"
                    description="No cohort enrolled students assigned to you yet."
                    className="py-10"
                  />
                ) : !isAnyVisibleGroupLoading && !hasAnyVisibleRows ? (
                  <EmptyState
                    icon={BookOpenIcon}
                    title="No students to show"
                    description="No cohort students assigned tou you yet."
                    className="py-10"
                  />
                ) : (
                  <div className="space-y-4">
                    {cohortsToRender.map((cohort) => {
                      const cohortId = cohort.id;
                      const groupHasLoaded =
                        enrolmentsLoadingByCohortId?.[cohortId] === false ||
                        enrolmentsByCohortId?.[cohortId] !== undefined;
                      const isLoading = Boolean(
                        enrolmentsLoadingByCohortId?.[cohortId]
                      );
                      const rows = groupedProcessedData.get(cohortId) || [];
                      const defaultExpanded = groupHasLoaded
                        ? rows.length > 0
                        : true;
                      const isExpanded =
                        expandedByCohortId?.[cohortId] ?? defaultExpanded;

                      return (
                        <div
                          key={cohortId}
                          className="rounded-md"
                        >
                          <div className="px-1 py-2 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1 min-w-0">
                              <Button
                                type="button"
                                variant="ghost"
                                size=""
                                className="px-2 -mt-2 text-(--neutral-900) hover:bg-transparent flex items-center justify-center"
                                onClick={() => toggleGroupExpanded(cohortId)}
                                aria-label={`Toggle cohort ${cohort.name}`}
                              >
                                <ChevronDown
                                  className={`w-6 h-6 transition-transform ${
                                    isExpanded ? "rotate-180" : "rotate-0"
                                  }`}
                                  strokeWidth={3.5}
                                />
                              </Button>
                              <div className="flex items-center gap-1 min-w-0">
                                <h4 className="font-heading text-(--neutral-900) truncate leading-none">
                                  Cohort: {cohort.name}
                                </h4>
                                {allowEnrolmentActions && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size=""
                                    className="p-0 -mt-2 h-auto w-auto text-(--neutral-800) hover:text-(--color-accent) hover:bg-transparent flex items-center justify-center"
                                    title="New Enrolments"
                                    onClick={() => {
                                      setBulkEnrollPrefillCohortId(String(cohortId));
                                      setBulkEnrollOpen(true);
                                    }}
                                    aria-label={`New enrolment for ${cohort.name}`}
                                  >
                                    <Plus className="w-5 h-5" strokeWidth={3.5} />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="px-4 pb-4">
                              {isLoading && rows.length === 0 ? (
                                <EnrolmentsSkeleton />
                              ) : (
                                <StudentTable
                                  data={rows}
                                  cohorts={cohorts}
                                  selectedCohortId={selectedCohortId}
                                  selectedCourse={selectedCourse}
                                  searchTerm={searchTerm}
                                  setSelectedCohortId={setSelectedCohortId}
                                  onEnrollmentDeleted={refreshEnrolments}
                                  onEnrollmentUpdated={refreshEnrolments}
                                  allowEnrolmentActions={allowEnrolmentActions}
                                  hideCohortColumn
                                  showSelectedCount
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </TabsContent>
          )}

          {showCohortsTab && activeTab === "cohorts" && (
          <TabsContent value="cohorts" className="space-y-3">
            {loading ? (
              <div className="flex justify-center items-center py-12 w-full">
                <Spinner />
              </div>
            ) : (
              <>
                <TableToolbar
                  searchTerm={cohortSearch}
                  onSearchChange={setCohortSearch}
                  filters={[
                    {
                      label: "Filter by Type",
                      value: pendingCohortTypeFilter,
                      onChange: setPendingCohortTypeFilter,
                      onClear: () => setPendingCohortTypeFilter("ALL"),
                      options: cohortTypeOptions.map((type) => ({
                        value: type,
                        label: type === "ALL" ? "All Types" : type,
                      })),
                    },
                  ]}
                  sorts={[
                    {
                      label: "Sort by",
                      value: pendingCohortSort,
                      onChange: setPendingCohortSort,
                      onClear: () => setPendingCohortSort("name"),
                      options: [
                        { value: "name", label: "Name" },
                        { value: "type", label: "Type" },
                        { value: "start", label: "Start Date" },
                        { value: "end", label: "End Date" },
                      ],
                    },
                  ]}
                  onApplyFilters={() => {
                    setCohortTypeFilter(pendingCohortTypeFilter);
                  }}
                  onApplySort={() => {
                    setCohortSort(pendingCohortSort);
                  }}
                />
                <CohortDataTable
                  columns={cohortColumns}
                  data={filteredSortedCohorts}
                  loading={loading}
                  onEdit={(row) => {
                    setEditCohort(row.raw || row);
                    setEditDialogOpen(true);
                  }}
                  onDelete={(row) => {
                    setDeleteCohort(row.raw || row);
                    setDeleteDialogOpen(true);
                  }}
                  onCohortsUpdated={refreshCohorts}
                />
              </>
            )}
          </TabsContent>
          )}
        </Tabs>
      </div>
      {allowEnrolmentActions && (
        <BulkEnrollStudents
          cohorts={cohorts}
          initialCohortId={bulkEnrollPrefillCohortId}
          open={bulkEnrollOpen}
          onEnrolled={() => {
            setBulkEnrollOpen(false);
            refreshEnrolments();
          }}
          onOpenChange={setBulkEnrollOpen}
          refetchCohorts={refreshCohorts}
          enrolledStudentIds={Array.from(new Set(enrolments.map((e) => e.id)))}
          showTrigger={false}
        />
      )}

      {allowEnrolmentActions && (
        <CohortBulkEnrollDialog
          open={cohortBulkDialogOpen}
          onOpenChange={setCohortBulkDialogOpen}
          cohorts={cohorts}
          onUploaded={refreshEnrolments}
        />
      )}

      {showCohortsTab && (
        <EditCohorts
          cohortId={editCohort?.id}
          initialData={editCohort || {}}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          refetchCohorts={refreshCohorts}
        />
      )}

      {showCohortsTab && (
        <DeleteCohort
          cohortId={deleteCohort?.id}
          cohortName={deleteCohort?.name}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onDeleted={refreshCohorts}
        />
      )}
    </>
  );
}
